import {
  Button,
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  Loader,
  Option,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Card, CardSending, CardSendingPageValidation, Parameter } from 'src/front/api/apiInstance';

import {
  CardDeliveryComboType,
  CardSelectionType,
  ReferenceDataType,
} from 'src/front/api/generated';

import usePRM from 'src/front/common/hooks/usePRM';
import { dictionaryLineFormat, optionFormat } from 'src/front/common/utils/formUtils';
import { CardSendingInformationSchema } from 'src/front/common/utils/validationSchemas';
import {
  CheckboxElement,
  CheckboxRow,
  Container,
  Form,
  FormElement,
  FormRow,
  Separator,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import {
  useCardTypeState,
  useProductOfferDebitState,
  useProductOfferState,
} from 'src/front/contexts/CardEntryContext';

const DELIVERY_COMBO_LABEL = {
  [CardDeliveryComboType.BRANCH]: 'cardEntry_cardInfo_sendingInfo_branch_label',
  [CardDeliveryComboType.COURIER]: 'cardEntry_cardInfo_sendingInfo_courier_label',
  [CardDeliveryComboType.POS]: 'cardEntry_cardInfo_sendingInfo_pos_label',
  [CardDeliveryComboType.NONE]: '',
};
type ComboEnableType = {
  enableCardDeliveryBranch?: boolean;
  enableCardDeliveryAddress?: boolean;
  enablePinCodeDeliveryBranch?: boolean;
  enablePinCodeDeliveryAddress?: boolean;
};

type SendingInformationType = {
  cardDeliveryBranchCode?: string;
  cardDeliveryAddressCode?: string;
  pinCodeDeliveryBranchCode?: string;
  pinCodeDeliveryAddressCode?: string;
};

const defaultComboEnables: ComboEnableType = {
  enableCardDeliveryAddress: true,
  enableCardDeliveryBranch: true,
  enablePinCodeDeliveryAddress: true,
  enablePinCodeDeliveryBranch: true,
};

interface FormData {
  cardDeliveryAddress: Option | null;
  cardDeliveryBranch: Option | null;
  instantEmbossing: boolean;
  deliveryComboType: CardDeliveryComboType;
  pinCodeDeliveryAddress: Option | null;
  pinCodeDeliveryBranch: Option | null;
  pinDeliveryComboType: CardDeliveryComboType;
  statementPeriod: Option | null;
  sendViaEmail: boolean;
  smsAlert: boolean;
  consentInfo: boolean;
  consentCommunicationsNspk: boolean;
  consentCheckRegistrationNumber: boolean;
  consentScoring: boolean;
  lifeInsurance: boolean;
}

const defaultValues = {
  cardDeliveryAddress: null,
  cardDeliveryBranch: null,
  deliveryComboType: CardDeliveryComboType.BRANCH,
  instantEmbossing: false,
  pinCodeDeliveryAddress: null,
  pinCodeDeliveryBranch: null,
  pinDeliveryComboType: CardDeliveryComboType.BRANCH,
  statementPeriod: null,
  sendViaEmail: false,
  smsAlert: false,
  lifeInsurance: false,
  consentInfo: true,
  consentCommunicationsNspk: true,
  consentCheckRegistrationNumber: true,
  consentScoring: true,
};

interface Props {
  customerId?: string;
  isProductOfferEdit?: boolean;
  goToNextStep: (customerId?: string) => void;
}

const CardSendingInformation: FC<Props> = ({ customerId, goToNextStep, isProductOfferEdit }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isProductOfferDebit = useProductOfferDebitState();
  const isCreditCardFlow = useCardTypeState() === CardSelectionType.CREDITCARD;
  const isCreditCard = !isProductOfferDebit && isCreditCardFlow;
  const deliveryAddresses = usePRM(ReferenceDataType.CARDSENDINGADDRESS);
  const [statementPeriods, setStatementPeriods] = useState<Option[]>([]);
  const [deliveryComboItems, setDeliveryComboItems] = useState<Option[]>([]);
  const [pinDeliveryComboItems, setPinDeliveryComboItems] = useState<Option[]>([]);
  const [comboEnables, setComboEnables] = useState<ComboEnableType>(defaultComboEnables);
  const [isConsentEnabled, setIsConsentEnabled] = useState<boolean>(false);
  const [isConsentCommunicationsNspkDisabled, setIsConsentCommunicationsNspkDisabled] = useState<
    boolean
  >(false);

  const productOfferState = useProductOfferState();

  const { handleSubmit, errors, register, watch, setValue, control, formState } = useForm<FormData>(
    {
      defaultValues,
      resolver: yupResolver(CardSendingInformationSchema, {
        abortEarly: false,
        context: { isCreditCard },
      }),
    }
  );

  useEffect(() => {
    register({ name: 'instantEmbossing' });
    register({ name: 'deliveryComboType' });
    register({ name: 'cardDeliveryBranch' });
    register({ name: 'pinDeliveryComboType' });
    register({ name: 'pinCodeDeliveryAddress' });
    register({ name: 'pinCodeDeliveryBranch' });
    register({ name: 'statementPeriod' });
    register({ name: 'sendViaEmail' });
    register({ name: 'smsAlert' });
    register({ name: 'consentInfo' });
    register({ name: 'consentCommunicationsNspk' });
    register({ name: 'consentCheckRegistrationNumber' });
    register({ name: 'consentScoring' });
    register({ name: 'lifeInsurance' });
  }, [register]);

  const getStatementPeriods = useCallback(async () => {
    const { data } = await Parameter.getReferenceData({
      referenceDataType: ReferenceDataType.CARDSTATEMENTGROUP,
    });

    setStatementPeriods(data.parameterDataList?.map(optionFormat) || []);
  }, []);

  useEffect(() => {
    if (isCreditCard) {
      getStatementPeriods();
    }
  }, [getStatementPeriods, isCreditCard]);

  const handleDeliveryAddressChange = useCallback(
    (onChange?: (item: Option | null) => void) => async (
      address: Option | null
    ): Promise<Option[]> => {
      if (onChange) {
        onChange(address);
        setValue('cardDeliveryBranch', null);
      } else {
        setValue('cardDeliveryAddress', address);
      }

      const response = await CardSending.getCardDeliveryCombo({
        cardDeliveryAddressCode: address?.value || '',
      });

      let deliveries: Option[] = [];

      if (response.data.result === 'OK') {
        deliveries = response?.data?.parameterDataList
          ? response.data.parameterDataList.map(optionFormat)
          : [];
        setValue('deliveryComboType', response.data.cardDeliveryComboType);
        setDeliveryComboItems(deliveries);
      }

      return deliveries;
    },
    [setValue]
  );

  const handlePinDeliveryAddressChange = useCallback(
    async (address: Option | null, sessionLoad?: boolean): Promise<Option[]> => {
      let pinDeliveryBranchItems: Option[] = [];
      setValue('pinCodeDeliveryAddress', address);
      if (!sessionLoad) {
        setValue('pinCodeDeliveryBranch', null);
      }

      const { data } = await CardSending.getCardDeliveryCombo({
        cardDeliveryAddressCode: address?.value || '',
      });

      if (data.result === 'OK') {
        setValue('pinDeliveryComboType', data.cardDeliveryComboType);
        pinDeliveryBranchItems = data.parameterDataList?.map(optionFormat) || [];
        setPinDeliveryComboItems(pinDeliveryBranchItems);
      }

      return pinDeliveryBranchItems;
    },
    [setValue]
  );

  const handleSetDeliveryValues = useCallback(
    async (data: SendingInformationType): Promise<void> => {
      const cardDeliveryAddress =
        deliveryAddresses.find((address) => address.value === data.cardDeliveryAddressCode) || null;

      const deliveries = await handleDeliveryAddressChange()(cardDeliveryAddress);

      const cardDeliveryBranch =
        deliveries.find((branch) => branch.value === data.cardDeliveryBranchCode) || null;

      setValue('cardDeliveryBranch', cardDeliveryBranch, {
        shouldValidate: true,
        shouldDirty: true,
      });
      const pinDeliveryAddress =
        deliveryAddresses.find(
          (pinDelivery) => pinDelivery.value === data.pinCodeDeliveryAddressCode
        ) || null;

      const pinDeliveries = await handlePinDeliveryAddressChange(pinDeliveryAddress);
      const cardPinDeliveryBranch =
        pinDeliveries.find((branch) => branch.value === data.pinCodeDeliveryBranchCode) || null;

      setValue('pinCodeDeliveryBranch', cardPinDeliveryBranch, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [deliveryAddresses, handleDeliveryAddressChange, handlePinDeliveryAddressChange, setValue]
  );

  const handleInstantEmbossChange = async ({
    target,
  }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValue('instantEmbossing', target.checked);
    if (target.checked) {
      const { data } = await CardSending.getInstantEmbossedParameters();

      if (data.result === 'OK') {
        handleSetDeliveryValues({
          cardDeliveryAddressCode: data.cardDeliveryAddressCode,
          cardDeliveryBranchCode: data.pinCodeDeliveryBranchCode,
          pinCodeDeliveryAddressCode: data.pinCodeDeliveryAddressCode,
          pinCodeDeliveryBranchCode: data.pinCodeDeliveryBranchCode,
        });
      }
    } else {
      setValue('cardDeliveryAddress', null);
      setValue('cardDeliveryBranch', null);
      setValue('pinCodeDeliveryAddress', null);
      setValue('pinCodeDeliveryBranch', null);
    }
  };
  useEffect(() => {
    const fillCombos = async (): Promise<void> => {
      if (productOfferState) {
        setValue('instantEmbossing', productOfferState);
        const { data } = await CardSending.getInstantEmbossedParameters();

        if (data.result === 'OK') {
          handleSetDeliveryValues({
            cardDeliveryAddressCode: data.cardDeliveryAddressCode,
            cardDeliveryBranchCode: data.pinCodeDeliveryBranchCode,
            pinCodeDeliveryAddressCode: data.pinCodeDeliveryAddressCode,
            pinCodeDeliveryBranchCode: data.pinCodeDeliveryBranchCode,
          });
        }
      }
    };
    fillCombos();
  }, [handleSetDeliveryValues, productOfferState, setValue]);
  useEffect(() => {
    const getConsentInfo = async (): Promise<void> => {
      const { data } = await Card.getNSPKConcentInfo();
      setIsConsentEnabled(data.isCardNSPKConcentVisible);
    };
    getConsentInfo();
  }, []);
  const loadFormFields = useCallback(async () => {
    const { data } = await CardSendingPageValidation.getCardSendingPageData();

    if (data.result === 'OK' && data.pageData) {
      const {
        smsAlert,
        cardDeliveryAddress,
        cardDeliveryBranch,
        instantEmbossing,
        lifeInsurance,
        sendViaEmail,
        statementPeriod,
        pinCodeDeliveryAddress,
        pinCodeDeliveryBranch,
        isCampaignFlag,
        consentCommunicationsNspk,
        consentCheckRegistrationNumber,
      } = data.pageData;
      [
        {
          name: 'cardDeliveryBranch',
          value: cardDeliveryBranch ? optionFormat(cardDeliveryBranch) : null,
        },

        { name: 'instantEmbossing', value: instantEmbossing },

        { name: 'smsAlert', value: smsAlert },
        { name: 'consentInfo', value: isCampaignFlag || false },
        { name: 'consentCommunicationsNspk', value: consentCommunicationsNspk || false },
        { name: 'consentCheckRegistrationNumber', value: consentCheckRegistrationNumber || false },
        {
          name: 'pinCodeDeliveryBranch',
          value: pinCodeDeliveryBranch ? optionFormat(pinCodeDeliveryBranch) : null,
        },
        ...(isCreditCard
          ? [
              { name: 'sendViaEmail', value: sendViaEmail },
              { name: 'lifeInsurance', value: lifeInsurance },
              {
                name: 'statementPeriod',
                value: statementPeriod ? optionFormat(statementPeriod) : null,
              },
            ]
          : []),
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

      const { data: cardSendingInfoData } = await CardSending.getCardSendingInfo();
      if (cardSendingInfoData.result === 'OK') {
        if (cardSendingInfoData.instantEmbossing) {
          await handleDeliveryAddressChange()(optionFormat(cardDeliveryAddress));
          if (pinCodeDeliveryAddress) {
            await handlePinDeliveryAddressChange(optionFormat(pinCodeDeliveryAddress), true);
          }
        } else {
          setComboEnables(cardSendingInfoData);
          handleSetDeliveryValues(cardSendingInfoData);
        }
      }
    }
    setIsLoading(false);
  }, [
    handleDeliveryAddressChange,
    handlePinDeliveryAddressChange,
    handleSetDeliveryValues,
    isCreditCard,
    setValue,
  ]);

  const onSubmit = async (form: FormData): Promise<void> => {
    const { data } = await CardSendingPageValidation.validateCardSendingPageData({
      cardDeliveryAddress: dictionaryLineFormat(form.cardDeliveryAddress),
      ...(form.cardDeliveryBranch
        ? { cardDeliveryBranch: dictionaryLineFormat(form.cardDeliveryBranch) }
        : {}),
      instantEmbossing: form.instantEmbossing,
      isCampaignFlag: form.consentInfo,
      consentCommunicationsNspk: form.consentCommunicationsNspk,
      consentCheckRegistrationNumber: form.consentCheckRegistrationNumber,
      smsAlert: form.smsAlert,
      ...(form.pinCodeDeliveryAddress
        ? { pinCodeDeliveryAddress: dictionaryLineFormat(form.pinCodeDeliveryAddress) }
        : {}),
      ...(form.pinCodeDeliveryBranch
        ? { pinCodeDeliveryBranch: dictionaryLineFormat(form.pinCodeDeliveryBranch) }
        : {}),
      ...(isCreditCard
        ? {
            lifeInsurance: form.lifeInsurance,
            sendViaEmail: form.sendViaEmail,
            consentScoring: form.consentScoring,
            ...(form.statementPeriod
              ? { statementPeriod: dictionaryLineFormat(form.statementPeriod) }
              : {}),
          }
        : {}),
    });

    if (data.result === 'OK') {
      goToNextStep(customerId);
    }
  };
  const handleComboChange = (name: string) => (option: Option | null) => {
    setValue(name, option);
  };
  const handleNspkConsentInfo = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setValue('consentInfo', target.checked);
    setValue('consentCommunicationsNspk', target.checked);
    setIsConsentCommunicationsNspkDisabled(!target.checked);
  };

  useEffect(() => {
    loadFormFields();
  }, [loadFormFields]);

  const comboType = watch('deliveryComboType') as CardDeliveryComboType;
  const pinComboType = watch('pinDeliveryComboType') as CardDeliveryComboType;

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_cardInfo_sendingInfo_title" />
      </Title>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="cardDeliveryAddress"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={watch('instantEmbossing') || !comboEnables.enableCardDeliveryAddress}
                  errorMessage={t(errors?.cardDeliveryAddress?.message as string)}
                  items={deliveryAddresses}
                  label={t('cardEntry_cardInfo_sendingInfo_cardSendingAddress_label')}
                  name="cardDeliveryAddress"
                  onChange={handleDeliveryAddressChange(onChange)}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
          {watch('cardDeliveryAddress') && comboType !== CardDeliveryComboType.NONE ? (
            <FormElement>
              <Combobox
                disabled={watch('instantEmbossing') || !comboEnables.enableCardDeliveryBranch}
                errorMessage={t(errors?.cardDeliveryBranch?.message as string)}
                items={deliveryComboItems}
                label={t(DELIVERY_COMBO_LABEL[comboType])}
                name="cardDeliveryBranch"
                onChange={handleComboChange('cardDeliveryBranch')}
                value={watch('cardDeliveryBranch')}
                required
                searchable
              />
            </FormElement>
          ) : null}
        </FormRow>
        <FormRow>
          <FormElement>
            <Checkbox
              disabled={productOfferState || isProductOfferEdit}
              id="instantEmbossing"
              label={t('cardEntry_cardInfo_sendingInfo_embossInDepartment_label')}
              onChange={handleInstantEmbossChange}
              value={watch('instantEmbossing') || productOfferState}
            />
          </FormElement>
        </FormRow>
        <Separator />

        <Subtitle>
          <Trans i18nKey="cardEntry_cardInfo_sendingInfo_shippingPinCode_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Combobox
              disabled={!comboEnables.enablePinCodeDeliveryAddress}
              errorMessage={t(errors?.pinCodeDeliveryAddress?.message as string)}
              items={deliveryAddresses}
              label={t('cardEntry_cardInfo_sendingInfo_shippingAddressPinCode_label')}
              name="pinCodeDeliveryAddress"
              onChange={handlePinDeliveryAddressChange}
              value={watch('pinCodeDeliveryAddress')}
              required
            />
          </FormElement>
          {watch('pinCodeDeliveryAddress') && pinComboType !== CardDeliveryComboType.NONE ? (
            <FormElement>
              <Combobox
                disabled={!comboEnables.enablePinCodeDeliveryBranch}
                errorMessage={t(errors?.pinCodeDeliveryBranch?.message as string)}
                items={pinDeliveryComboItems}
                label={t(DELIVERY_COMBO_LABEL[pinComboType])}
                name="pinCodeDeliveryBranch"
                onChange={handleComboChange('pinCodeDeliveryBranch')}
                value={watch('pinCodeDeliveryBranch')}
                required
                searchable
              />
            </FormElement>
          ) : null}
        </FormRow>
        {isCreditCard ? (
          <>
            <Subtitle>
              <Trans i18nKey="cardEntry_cardInfo_sendingInfo_statement_subtitle" />
            </Subtitle>
            <FormRow>
              <FormElement>
                <Combobox
                  errorMessage={t(errors?.statementPeriod?.message as string)}
                  items={statementPeriods}
                  label={t('cardEntry_cardInfo_sendingInfo_receiptOfStatement_label')}
                  name="statementPeriod"
                  onChange={handleComboChange('statementPeriod')}
                  value={watch('statementPeriod')}
                  required
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Checkbox
                  id="sendViaEmail"
                  label={t('cardEntry_cardInfo_sendingInfo_sendStatementByEmail_label')}
                  onChange={({ target }) => setValue('sendViaEmail', target.checked)}
                  value={watch('sendViaEmail')}
                />
              </FormElement>
            </FormRow>
          </>
        ) : null}
        <Separator />
        <Subtitle>
          <Trans i18nKey="cardEntry_cardInfo_sendingInfo_otherInfo_subtitle" />
        </Subtitle>
        {isConsentEnabled ? (
          <>
            <CheckboxRow>
              <CheckboxElement>
                <Checkbox
                  id="nspkConsentInfo"
                  label={t('cardEntry_sendingInformation_mirLoyalty_text')}
                  onChange={handleNspkConsentInfo}
                  value={watch('consentInfo')}
                />
              </CheckboxElement>
            </CheckboxRow>
            <CheckboxRow>
              <CheckboxElement>
                <Checkbox
                  disabled={isConsentCommunicationsNspkDisabled}
                  id="consentCommunicationsNspk"
                  label={t('cardEntry_sendingInformation_nspcConsent_text')}
                  onChange={({ target }) => setValue('consentCommunicationsNspk', target.checked)}
                  value={watch('consentCommunicationsNspk')}
                />
              </CheckboxElement>
            </CheckboxRow>
          </>
        ) : null}
        <CheckboxRow>
          <FormElement>
            <Checkbox
              id="smsAlert"
              label={t('cardEntry_cardInfo_sendingInfo_smsTransactionAlert_label')}
              onChange={({ target }) => setValue('smsAlert', target.checked)}
              value={watch('smsAlert')}
            />
          </FormElement>
        </CheckboxRow>
        {isCreditCard ? (
          <CheckboxRow>
            <FormElement>
              <Checkbox
                id="lifeInsurance"
                label={t('cardEntry_cardInfo_sendingInfo_lifeInsurance_label')}
                onChange={({ target }) => setValue('lifeInsurance', target.checked)}
                value={watch('lifeInsurance')}
              />
            </FormElement>
          </CheckboxRow>
        ) : null}
        {isCreditCard ? (
          <>
            <Subtitle>
              <Trans i18nKey="cardEntry_cardInfo_sendingInfo_consentCheckRegistrationNumber_subtitle" />
            </Subtitle>
            <CheckboxRow>
              <CheckboxElement>
                <Checkbox
                  id="consentCheckRegistrationNumber"
                  label={t('cardEntry_sendingInformation_consentCheckRegistrationNumber_text')}
                  onChange={({ target }) =>
                    setValue('consentCheckRegistrationNumber', target.checked)
                  }
                  value={watch('consentCheckRegistrationNumber')}
                  disabled
                />
              </CheckboxElement>
            </CheckboxRow>
          </>
        ) : null}
        {isCreditCard ? (
          <>
            <Subtitle>
              <Trans i18nKey="cardEntry_cardInfo_sendingInfo_consentScoring_subtitle" />
            </Subtitle>
            <CheckboxRow>
              <CheckboxElement>
                <Checkbox
                  id="consentScoring"
                  label={t('cardEntry_sendingInformation_consentScoring_text')}
                  onChange={({ target }) => setValue('consentScoring', target.checked)}
                  value={watch('consentScoring')}
                />
              </CheckboxElement>
            </CheckboxRow>
          </>
        ) : null}
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default CardSendingInformation;
